// src/layout/index.tsx
import { useEffect } from "react";
import { useLocation } from "react-router";
import { useTheme } from "next-themes";
import Footer from "./footer";
import NavBar from "./nav-bar";
import { ExamStrip } from "./ExamStrip";
import { RouteProgress } from "@/components/RouteProgress";
import { Toaster } from "@/components/ui/sonner";
import { PassGateDialog } from "@/components/pass/PassGateDialog";

/**
 * Routes that own the full window.
 *
 * A timed attempt must not show a nav bar inviting the student to click
 * away mid-paper, and onboarding is a flow rather than a page.
 */
const CHROMELESS_PREFIXES = ["/onboarding", "/contests/j/"];

function isChromeless(pathname: string): boolean {
  if (CHROMELESS_PREFIXES.some((p) => pathname.startsWith(p))) return true;
  // The attempt runners themselves — but NOT their results pages, which
  // are normal pages and should get the chrome back.
  if (/^\/mock-tests\/attempt\/[^/]+$/.test(pathname)) return true;
  if (/^\/contests\/[^/]+\/play$/.test(pathname)) return true;
  return false;
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { resolvedTheme } = useTheme();
  const bare = isChromeless(pathname);

  // Navigating should land you at the top of the new page. Without this,
  // going from a long paper list to Settings drops you halfway down it.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname]);

  return (
    <div className="flex min-h-screen flex-col">
      <RouteProgress />

      {!bare && (
        <>
          <ExamStrip />
          <NavBar />
        </>
      )}

      <main className="flex-1">
        {/* Keyed on pathname so each route plays the rise-in once. Keyed on
the path only — not search params — so paginating or switching a
tab doesn't re-animate the whole page. */}
        <div
          key={pathname}
          className="animate-rise-in motion-reduce:animate-none"
        >
          {children}
        </div>
      </main>

      <PassGateDialog />
      <Toaster
        richColors
        // Was hardcoded to "light", which left toasts glowing white in dark
        // mode.
        theme={resolvedTheme === "dark" ? "dark" : "light"}
        position="bottom-right"
      />

      {!bare && <Footer />}
    </div>
  );
}
